import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { SliderInput } from './SliderInput';
import { InvestmentTypeSelector } from './InvestmentTypeSelector';
import { FrequencySelector } from './FrequencySelector';
import { ContributionPause } from './ContributionPause';
import { Calculator as CalcIcon } from 'lucide-react';
import { INVESTMENT_TYPES } from '../../utils/taxEngine';

export const CalculatorInputs = () => {
  const { inputs, updateInput, t, currency } = useApp();
  
  const isUnitLinked = inputs.investmentType === INVESTMENT_TYPES.UNIT_LINKED;
  const isSavingsAccount = inputs.investmentType === INVESTMENT_TYPES.SAVINGS_ACCOUNT;
  
  // Interest rate range depends on investment type
  const interestRateConfig = isSavingsAccount 
    ? { min: 1, max: 4, step: 0.1 }  // Cont de economii: 1-4%
    : { min: 0, max: 25, step: 0.5 }; // Unit Linked: 0-25%
  
  // Currency suffix
  const currencySuffix = currency === 'EUR' ? ' EUR' : ' RON';
  
  return (
    <Card 
      className="bg-card border border-border shadow-sm rounded-2xl overflow-hidden"
      data-testid="calculator-inputs"
    >
      <CardHeader className="pb-4 border-b border-border/50">
        <CardTitle className="flex items-center gap-3 text-xl font-light tracking-tight">
          <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500/10 to-emerald-500/10">
            <CalcIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          {t('calculator')}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-8">
        {/* Investment type selector - FIRST */}
        <InvestmentTypeSelector />
        
        {/* Main inputs */}
        <div className="space-y-6">
          <SliderInput
            label={t('initialDeposit')}
            value={inputs.initialDeposit}
            onChange={(value) => updateInput('initialDeposit', value)}
            min={0}
            max={500000}
            step={1000}
            suffix={currencySuffix}
            tooltip={t('tooltipInitialDeposit')}
            testId="initial-deposit"
          />
          
          <SliderInput
            label={t('monthlyContribution')}
            value={inputs.monthlyContribution}
            onChange={(value) => updateInput('monthlyContribution', value)}
            min={0}
            max={10000}
            step={50}
            suffix={currencySuffix}
            tooltip={t('tooltipMonthlyContribution')}
            testId="monthly-contribution"
          />
          
          <SliderInput
            label={t('annualInterestRate')}
            value={inputs.annualInterestRate}
            onChange={(value) => updateInput('annualInterestRate', value)}
            min={interestRateConfig.min}
            max={interestRateConfig.max}
            step={interestRateConfig.step}
            suffix="%"
            tooltip={t('tooltipInterestRate')}
            testId="interest-rate"
          />
          
          <SliderInput
            label={t('investmentPeriod')}
            value={inputs.years}
            onChange={(value) => updateInput('years', value)}
            min={1}
            max={50}
            step={1}
            suffix={inputs.years === 1 ? ' an' : ' ani'}
            tooltip={t('tooltipPeriod')}
            testId="investment-period"
          />
        </div>
        
        {/* Frequency selectors - only for Unit Linked */}
        {isUnitLinked && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FrequencySelector type="compounding" />
            <FrequencySelector type="contribution" />
          </div>
        )}
        
        {/* Contribution pause */}
        <ContributionPause />
      </CardContent>
    </Card>
  );
};

export default CalculatorInputs;
